import React from 'react';
import { GraduationCapIcon, CheckIcon, LockIcon } from 'lucide-react';
import { areaStudyOptions, FilterOption } from '../data/filterOptions';

// Visual shell for the /request-information/v3 template: the marketing
// copy stays pinned on the left while every question is asked inside a
// single persistent card on the right.

export interface SplitCardFormValues {
  areaOfStudy: number;
  program: number;
  highestEducation: string;
  hsGraduationYear: string;
  startDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
}
export interface RequestInfoSplitCardProps {
  badge: string;
  headline: string;
  headlineAccent: string;
  subhead: string;
  features: string[];
  // Flow position. `step` matches the shared flow (0 = program selection,
  // 1 = education, 2 = grad year, 3 = timeframe, 5 = address, 6 = contact).
  // `stage` splits step 0 into area (0) then program (1).
  step: number;
  stage: number;
  formData: SplitCardFormValues;
  availablePrograms: FilterOption[];
  years: number[];
  error: string;
  tcpaLoading: boolean;
  tcpaError: string;
  tcpaDisclaimerHtml: string | null;
  onFieldChange: (field: keyof SplitCardFormValues, value: string | number | boolean) => void;
  onAreaSelect: (area: number) => void;
  onZipChange: (zip: string) => void;
  onPhoneChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onContinue: () => void;
  onBack: () => void;
}

// Visual Arts & Graphic Design (12) isn't offered on this template.
const HIDDEN_AREA_VALUES = [12];
const AREA_TILES = areaStudyOptions.filter(option => !HIDDEN_AREA_VALUES.includes(option.value));
const STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
const START_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'immediately',
  label: 'Immediately'
}, {
  value: '1-3-months',
  label: 'Within 1–3 months'
}, {
  value: '3-6-months',
  label: 'Within 3–6 months'
}, {
  value: '6-12-months',
  label: 'Within 6–12 months'
}, {
  value: '1-year-plus',
  label: 'More than a year out'
}, {
  value: 'undecided',
  label: 'Undecided'
}];
const EDUCATION_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'high-school',
  label: 'High School Diploma/GED'
}, {
  value: 'some-college',
  label: 'Some College'
}, {
  value: 'associates',
  label: "Associate's Degree"
}, {
  value: 'bachelors',
  label: "Bachelor's Degree"
}, {
  value: 'masters',
  label: "Master's Degree"
}, {
  value: 'doctorate',
  label: 'Doctorate'
}, {
  value: 'some-high-school',
  label: 'Some High School'
}];
const FALLBACK_CONSENT = 'By checking this box and clicking "See My Matches," I provide my electronic signature and express written consent for findonlinedegrees.org and up to four (4) partner schools to contact me at the phone number and email provided above — including by SMS/text messages and calls using an automatic telephone dialing system or an artificial/prerecorded voice — regarding educational opportunities. Message and data rates may apply. Message frequency varies. Consent is not a condition of enrollment or purchase. I can revoke consent at any time.';
const CARD_COPY: Record<string, {
  title: string;
  subtitle: string;
}> = {
  area: {
    title: "Let's Get Started",
    subtitle: 'Select your area of interest'
  },
  program: {
    title: 'Narrow it down',
    subtitle: 'Pick the program closest to your interest'
  },
  education: {
    title: 'Your background',
    subtitle: 'This helps us match you to programs you qualify for'
  },
  gradYear: {
    title: 'Almost there',
    subtitle: 'What year did you graduate high school?'
  },
  timing: {
    title: 'Timing',
    subtitle: 'When would you like to start classes?'
  },
  address: {
    title: 'Where are you located?',
    subtitle: 'Used to match you with programs available in your state'
  },
  contact: {
    title: 'Almost done',
    subtitle: 'Where should schools reach you?'
  }
};
const selectClasses = (filled: boolean) => `w-full px-4 py-3 bg-white border rounded-xl text-gray-900 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#e8594f]/30 ${filled ? 'border-[#e8594f]' : 'border-gray-200 hover:border-gray-300'}`;
const inputClasses = 'w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 transition-colors placeholder:text-gray-400 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/30 hover:border-gray-300';
const chevronStyle = {
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: 'right 0.75rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.25em 1.25em',
  paddingRight: '2.5rem'
};
const FieldLabel = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-322" data-mp="3k6imzxcjg4d5">
    {children} <span className="text-[#e8594f]" data-id="element-323" data-mp="3f7pao88zmjek">*</span>
  </span>;
export function RequestInfoSplitCard({
  badge,
  headline,
  headlineAccent,
  subhead,
  features,
  step,
  stage,
  formData,
  availablePrograms,
  years,
  error,
  tcpaLoading,
  tcpaError,
  tcpaDisclaimerHtml,
  onFieldChange,
  onAreaSelect,
  onZipChange,
  onPhoneChange,
  onContinue,
  onBack
}: RequestInfoSplitCardProps) {
  const screen = step === 0 ? stage === 0 ? 'area' : 'program' : step === 1 ? 'education' : step === 2 ? 'gradYear' : step === 3 ? 'timing' : step === 5 ? 'address' : 'contact';
  const displayStep = screen === 'area' ? 1 : screen === 'program' ? 2 : screen === 'education' ? 3 : screen === 'gradYear' ? 4 : screen === 'timing' ? 5 : screen === 'address' ? 6 : 7;
  const canGoBack = !(step === 0 && stage === 0);
  const isFinalStep = screen === 'contact';
  const copy = CARD_COPY[screen];
  return <div className="min-h-screen bg-[#f4f5f7] flex flex-col" data-id="element-324" data-mp="2ezirz1e8zy8w">
      {/* Advertising disclosure */}
      <div className="bg-gray-100 border-b border-gray-200 py-2.5 px-4" data-id="element-325" data-mp="2jylry7fdfea0">
        <p className="text-center text-xs text-gray-600" data-id="element-326" data-mp="2jwj33qpyfr6v">
          We earn commissions from schools listed on this site, which influences
          how listings are presented.{' '}
          <a href="/terms" className="underline hover:text-gray-900" data-id="element-327" data-mp="3cmrduxi5v9bf">
            Advertising Disclosure
          </a>
        </p>
      </div>

      {/* Brand bar */}
      <header className="bg-white border-b border-gray-200 py-4 px-4" data-id="element-328" data-mp="32obtrehjx0bf">
        <a href="/" className="flex items-center justify-center gap-2.5" data-id="element-329" data-mp="1n8ctepe6e8lq">
          <span className="w-9 h-9 rounded-lg bg-[#e8594f] flex items-center justify-center" data-id="element-330" data-mp="1nafrzxte9a22">
            <GraduationCapIcon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-331" data-mp="1r7twxda1kvo9" />
          </span>
          <span className="text-xl font-bold text-gray-900 tracking-tight" data-id="element-332" data-mp="1jsi9sejeu4nb">
            FindOnlineDegrees
          </span>
        </a>
      </header>

      <main className="flex-1 px-4 py-8 md:py-16" data-id="element-333" data-mp="1tov1x7zb4afy">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-334" data-mp="1tmiliz976850">
          {/* Marketing column — below the card on mobile */}
          <div className="order-2 lg:order-1" data-id="element-335" data-mp="110z7yb14gwmv">
            <div className="text-center lg:text-left" data-id="element-336" data-mp="10xtekjlgn8lc">
              <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-337" data-mp="w1w0kjxo6ebq">
                <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-338" data-mp="1ark47rtbpp2m" />
                {badge}
              </div>
            </div>
            <h1 className="text-center lg:text-left text-4xl sm:text-5xl font-extrabold text-gray-900 leading-[1.05] tracking-tight mb-6" data-id="element-339" data-mp="i8wl2hxw0k46">
              {headline}
              <span className="block text-[#e8594f]" data-id="element-340" data-mp="d8ip3y9k90mi">{headlineAccent}</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-7 max-w-md" data-id="element-341" data-mp="3nyit3pbm3r90">
              {subhead}
            </p>
            <ul className="space-y-3" data-id="element-342" data-mp="1pg7vn4a2ifr">
              {features.map(feature => <li key={feature} className="flex items-center gap-3 text-gray-800" data-id="element-343" data-mp="5qtdbw3gkjm5">
                  <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-344" data-mp="5ogkgnw5woub" />
                  <span data-id="element-345" data-mp="ao1vhwolwnne">{feature}</span>
                </li>)}
            </ul>
          </div>

          {/* Question card — first on mobile so it lands above the fold */}
          <div className="order-1 lg:order-2 bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)] p-6 sm:p-8" data-id="element-346" data-mp="kghb4ncfl054">
            <div className="text-center mb-6" data-id="element-347" data-mp="tgdxujf9foxj">
              <p className="font-mono text-xs tracking-[0.18em] text-[#e8594f] mb-2" data-id="element-348" data-mp="thoxq85l4cjd">
                STEP {displayStep} OF 7
              </p>
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-349" data-mp="yd4eb7uz8mjh">
                {copy.title}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-350" data-mp="2dyod40xmwuty">{copy.subtitle}</p>
            </div>

            {screen === 'area' && <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="group" aria-label="Area of interest" data-id="element-351" data-mp="29xuot6ga5e0j">
                {AREA_TILES.map(option => {
              const selected = formData.areaOfStudy === option.value;
              return <button key={option.value} type="button" onClick={() => onAreaSelect(option.value)} aria-pressed={selected} className={`text-center px-3 py-4 rounded-xl border transition-all ${selected ? 'border-[#e8594f] bg-[#e8594f]/5 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-352" data-mp="2xme260e6sgzh">
                      <span className={`mx-auto mb-2 flex w-4 h-4 items-center justify-center rounded border ${selected ? 'border-[#e8594f] bg-[#e8594f]' : 'border-gray-300 bg-white'}`} aria-hidden="true" data-id="element-353" data-mp="2tf38mf7lxg2m">
                        {selected && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-354" data-mp="3cdfhdzulwnxo" />}
                      </span>
                      <span className="block text-sm font-semibold text-gray-900 leading-snug" data-id="element-355" data-mp="37euj45yx0ebv">
                        {option.label}
                      </span>
                    </button>;
            })}
              </div>}

            {screen === 'program' && <label className="block" data-id="element-356" data-mp="3v3dskotpax5j">
                <FieldLabel data-id="element-357" data-mp="3v4gkn02rf5n1">Program of Interest</FieldLabel>
                <select value={formData.program || ''} onChange={e => onFieldChange('program', Number(e.target.value))} className={selectClasses(Boolean(formData.program))} style={chevronStyle} data-id="element-358" data-mp="3q5c6rw4w4jqd">
                  <option value="" data-id="element-359" data-mp="4kthfo3wx6lk">Select a program</option>
                  {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-360" data-mp="8ldfctwb8e8r">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'education' && <label className="block" data-id="element-361" data-mp="3vsnwt5b0q8c1">
                <FieldLabel data-id="element-362" data-mp="3h2q369ubkqfg">Highest Education Level</FieldLabel>
                <select value={formData.highestEducation} onChange={e => onFieldChange('highestEducation', e.target.value)} className={selectClasses(Boolean(formData.highestEducation))} style={chevronStyle} data-id="element-363" data-mp="3m11buh8vouw3">
                  <option value="" data-id="element-364" data-mp="1ik4r78nyfbpy">Select one</option>
                  {EDUCATION_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-365" data-mp="1dtoexrvvdw56">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'gradYear' && <label className="block" data-id="element-366" data-mp="14ukuokkyvap3">
                <FieldLabel data-id="element-367" data-mp="21iqu8t317cwi">HS Graduation Year</FieldLabel>
                <select value={formData.hsGraduationYear} onChange={e => onFieldChange('hsGraduationYear', e.target.value)} className={selectClasses(Boolean(formData.hsGraduationYear))} style={chevronStyle} data-id="element-368" data-mp="21i7m2436o2vo">
                  <option value="" data-id="element-369" data-mp="1niy717qpreta">Select year</option>
                  {years.map(year => <option key={year} value={year} data-id="element-370" data-mp="h4gqoc3v98xk">
                      {year}
                    </option>)}
                  <option value="before-1965" data-id="element-371" data-mp="2nrefc12l6544">Before 1965</option>
                </select>
              </label>}

            {screen === 'timing' && <label className="block" data-id="element-372" data-mp="2sqqzge22oy30">
                <FieldLabel data-id="element-373" data-mp="2wsckwnsoakot">Class Start Timeframe</FieldLabel>
                <select value={formData.startDate} onChange={e => onFieldChange('startDate', e.target.value)} className={selectClasses(Boolean(formData.startDate))} style={chevronStyle} data-id="element-374" data-mp="2wqt48w33u7mi">
                  <option value="" data-id="element-375" data-mp="3bfp78eqb0ug9">Select timeframe</option>
                  {START_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-376" data-mp="3gglen29v6yqf">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'address' && <div className="space-y-4" data-id="element-377" data-mp="1q34azffbv59u">
                <label className="block" data-id="element-378" data-mp="1q45um8aet036">
                  <FieldLabel data-id="element-379" data-mp="ut7g4q79r29f">Street Address</FieldLabel>
                  <input type="text" value={formData.streetAddress} onChange={e => onFieldChange('streetAddress', e.target.value)} placeholder="123 Main St" autoComplete="street-address" className={inputClasses} data-id="element-380" data-mp="urnzuzlm4r7n" />
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-381" data-mp="c412vip8v2v0">
                  <label className="block" data-id="element-382" data-mp="73wkto8ou02o">
                    <FieldLabel data-id="element-383" data-mp="757prrdvddvd">City</FieldLabel>
                    <input type="text" value={formData.city} onChange={e => onFieldChange('city', e.target.value)} autoComplete="address-level2" className={inputClasses} data-id="element-384" data-mp="lssutqub1n5x" />
                  </label>
                  <label className="block" data-id="element-385" data-mp="3kjku6c6cub7f">
                    <FieldLabel data-id="element-386" data-mp="3kgoxer68tskk">State</FieldLabel>
                    <select value={formData.state} onChange={e => onFieldChange('state', e.target.value)} className={selectClasses(Boolean(formData.state))} style={chevronStyle} data-id="element-387" data-mp="200ig7az36rgi">
                      <option value="" data-id="element-388" data-mp="1v15tqrc4nmeg">State</option>
                      {STATES.map(abbr => <option key={abbr} value={abbr} data-id="element-389" data-mp="2iqqgzmfbd5tj">
                          {abbr}
                        </option>)}
                    </select>
                  </label>
                  <label className="block" data-id="element-390" data-mp="29r3cusv79lws">
                    <FieldLabel data-id="element-391" data-mp="29qaeogab44q9">ZIP Code</FieldLabel>
                    <input type="text" inputMode="numeric" maxLength={5} value={formData.zipCode} onChange={e => onZipChange(e.target.value.replace(/\D/g, '').slice(0, 5))} autoComplete="postal-code" className={inputClasses} data-id="element-392" data-mp="2ep54i26oytjv" />
                  </label>
                </div>
              </div>}

            {screen === 'contact' && <div className="space-y-4" data-id="element-393" data-mp="2v8jcbg2eygay">
                <div className="grid grid-cols-2 gap-3" data-id="element-394" data-mp="2v7hpkec1c5de">
                  <label className="block" data-id="element-395" data-mp="2r7h9kp9afm48">
                    <FieldLabel data-id="element-396" data-mp="2r6fvebwz6jia">First Name</FieldLabel>
                    <input type="text" value={formData.firstName} onChange={e => onFieldChange('firstName', e.target.value)} autoComplete="given-name" className={inputClasses} data-id="element-397" data-mp="2m9yxwr3vyssj" />
                  </label>
                  <label className="block" data-id="element-398" data-mp="28br6sztj6lpa">
                    <FieldLabel data-id="element-399" data-mp="ajwpjearr2zr">Last Name</FieldLabel>
                    <input type="text" value={formData.lastName} onChange={e => onFieldChange('lastName', e.target.value)} autoComplete="family-name" className={inputClasses} data-id="element-400" data-mp="ahaidfrsipi2" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-401" data-mp="1y6iyv5lnxozy">
                  <label className="block" data-id="element-402" data-mp="2271nyrw68gyg">
                    <FieldLabel data-id="element-403" data-mp="225rpw2pbj8rt">Email Address</FieldLabel>
                    <input type="email" value={formData.email} onChange={e => onFieldChange('email', e.target.value)} autoComplete="email" className={inputClasses} data-id="element-404" data-mp="15h2626u4s637" />
                  </label>
                  <label className="block" data-id="element-405" data-mp="1jgtpwo7bwgp4">
                    <FieldLabel data-id="element-406" data-mp="1jegzrnrf300z">Phone Number</FieldLabel>
                    <input type="tel" value={formData.phone} onChange={onPhoneChange} placeholder="(555) 555-5555" autoComplete="tel" className={inputClasses} data-id="element-407" data-mp="2pvm0edm60l8w" />
                  </label>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-id="element-408" data-mp="2fbgpiw15zacm">
                  {tcpaLoading ? <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-409" data-mp="2ak8ha3bklv11">
                      <span className="w-4 h-4 border-2 border-gray-200 border-t-[#e8594f] rounded-full animate-spin" aria-hidden="true" data-id="element-410" data-mp="2p9o79h3a1wfo" />
                      Loading consent terms…
                    </div> : tcpaError ? <p className="text-sm text-red-600" data-id="element-411" data-mp="2y91hwd350r1z">{tcpaError}</p> : <label className="flex items-start gap-3 cursor-pointer" data-id="element-412" data-mp="2ua2e4vt570c4">
                      <input type="checkbox" checked={formData.consent} onChange={e => onFieldChange('consent', e.target.checked)} className="mt-0.5 w-4 h-4 flex-shrink-0 rounded border-gray-300 text-[#e8594f] focus:ring-[#e8594f]" data-id="element-413" data-mp="13vjpabnvl403" />
                      {tcpaDisclaimerHtml ? <span className="text-xs text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{
                  __html: tcpaDisclaimerHtml
                }} data-id="element-414" data-mp="g1rpekzmcp63" /> : <span className="text-xs text-gray-600 leading-relaxed" data-id="element-415" data-mp="61hfb98vrxv5">
                          {FALLBACK_CONSENT} I also agree to the{' '}
                          <a href="/privacy-policy" className="underline hover:text-gray-900" data-id="element-416" data-mp="3t89y6hdgf1nc">
                            Privacy Policy
                          </a>{' '}
                          and{' '}
                          <a href="/terms" className="underline hover:text-gray-900" data-id="element-417" data-mp="1rh6mrpvqpj1q">
                            Terms of Use
                          </a>
                          .
                        </span>}
                    </label>}
                </div>
              </div>}

            {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3" data-id="element-418" data-mp="2q18eqj8k02ev">
                <p className="text-sm text-red-600" data-id="element-419" data-mp="2pzdpwtu5vq5i">{error}</p>
              </div>}

            <div className={`mt-6 ${canGoBack ? 'flex gap-3' : ''}`} data-id="element-420" data-mp="2uy8g4gugkgzn">
              {canGoBack && <button type="button" onClick={onBack} className="px-6 py-3.5 rounded-xl border border-gray-200 bg-white font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300" data-id="element-421" data-mp="3io2vp665wjnl">
                  ← Back
                </button>}
              <button type="button" onClick={onContinue} className="flex-1 w-full px-6 py-3.5 rounded-xl bg-[#e8594f] font-bold text-white transition-colors hover:bg-[#d64a41] focus:outline-none focus:ring-2 focus:ring-[#e8594f]/40" data-id="element-422" data-mp="1y8hfs0zhjzlx">
                {isFinalStep ? 'See My Matches →' : 'Continue →'}
              </button>
            </div>

            {isFinalStep && <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500" data-id="element-423" data-mp="1zr7wdpjz2fby">
                <LockIcon className="w-3 h-3" aria-hidden="true" data-id="element-424" data-mp="1c1n2wcq6zzof" />
                Your information is secure and confidential
              </p>}
          </div>
        </div>
      </main>

      <footer className="py-8 px-4" data-id="element-425" data-mp="1lrgbd4v1fyog">
        <p className="text-center font-mono text-xs text-gray-500" data-id="element-426" data-mp="1lsi02c4z8jon">
          findonlinedegrees.org &nbsp;·&nbsp;
          <a href="/privacy-policy" className="underline hover:text-gray-700" data-id="element-427" data-mp="1gzyearw4yi8r">
            Privacy Policy
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/terms" className="underline hover:text-gray-700" data-id="element-428" data-mp="oc2kbuxyb4fo">
            Terms of Use
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/ca-notice" className="underline hover:text-gray-700" data-id="element-429" data-mp="1dz59qmqlcmel">
            Do Not Sell My Info
          </a>
        </p>
      </footer>
    </div>;
}